import axios from "axios";

const CACHE_TTL_MS = 30 * 1000;
const CACHE_PREFIX = "employee_api_get_cache:";
const pendingRequests = new Map();

const defaultAdapter = axios.getAdapter(axios.defaults.adapter);

const stableParams = (value) => {
  if (!value || typeof value !== "object") return value || null;
  if (Array.isArray(value)) return value.map(stableParams);

  return Object.keys(value)
    .sort()
    .reduce((acc, key) => {
      acc[key] = stableParams(value[key]);
      return acc;
    }, {});
};

const getCacheKey = (config) => {
  const token = localStorage.getItem("employee_token") || "";
  return `${CACHE_PREFIX}${JSON.stringify({
    token,
    baseURL: config.baseURL || "",
    url: config.url || "",
    params: stableParams(config.params),
  })}`;
};

const isCacheableGet = (config) => {
  const method = String(config.method || "get").toLowerCase();
  const url = String(config.url || "");
  return method === "get" && config.cache !== false && !url.includes("/auth/me");
};

const readCache = (key) => {
  try {
    const cached = JSON.parse(sessionStorage.getItem(key) || "null");
    if (!cached || Date.now() - cached.savedAt > CACHE_TTL_MS) return null;
    return cached.response;
  } catch {
    sessionStorage.removeItem(key);
    return null;
  }
};

const writeCache = (key, response) => {
  try {
    sessionStorage.setItem(key, JSON.stringify({
      savedAt: Date.now(),
      response: {
        data: response.data,
        status: response.status,
        statusText: response.statusText,
        headers: response.headers,
      },
    }));
  } catch {
    // Storage can be full or disabled; the request itself should still succeed.
  }
};

export const clearApiCache = () => {
  Object.keys(sessionStorage)
    .filter((key) => key.startsWith(CACHE_PREFIX))
    .forEach((key) => sessionStorage.removeItem(key));
  pendingRequests.clear();
};

export const createCacheAdapter = () => async (config) => {
  if (!isCacheableGet(config)) {
    return defaultAdapter(config);
  }

  const key = getCacheKey(config);
  const cached = readCache(key);
  if (cached) {
    return {
      ...cached,
      config,
      request: null,
      cached: true,
    };
  }

  if (pendingRequests.has(key)) {
    return pendingRequests.get(key);
  }

  const request = defaultAdapter(config)
    .then((response) => {
      writeCache(key, response);
      return response;
    })
    .finally(() => {
      pendingRequests.delete(key);
    });

  pendingRequests.set(key, request);
  return request;
};
